import * as React from 'react';
import { StaffRepository } from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';
import type { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';
import type { Staff } from '../../MedicareProAPI/Entities/Staff/Staff';
import { AgencyDownlineRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyDownlineRepository';
import { UserIpWhitelistRow } from '../Components/UserIpWhitelistRow';

type IpSubjectType = 'Agent' | 'Staff';

type IpSubject = {
  id: number;
  name: string;
  type: IpSubjectType;
  entity: any;
};

interface AgencyUserIpManagementPanelProps {
  userAccessToken: string;
  userId: number;
  agencyId: number;
  thisAgency: Agency;
}

/**
 * Panel for Agency managing per-user IP whitelist
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-12-03
 * @lastUpdated
 * @version 1.1.0
 */
export const AgencyUserIpManagementPanel = ({
  userAccessToken,
  userId,
  agencyId,
}: AgencyUserIpManagementPanelProps) => {
  const lastFetchedOffsetRef = React.useRef<number>(-1);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const [downlines, setDownlines] = React.useState<AgencyDownline[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [offset, setOffset] = React.useState<number>(0);
  const [manage, setManage] = React.useState<boolean>(true);
  const [staffList, setStaffList] = React.useState<Array<Staff>>([]);

  const PAGE_SIZE = 250;

  const agencyDownlineRepository = React.useMemo(() => {
    const r = new AgencyDownlineRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const staffRepository = React.useMemo(() => {
    const r = new StaffRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const activeDownlines = React.useMemo(() => {
    const keep = new Set(['A', 'T']);

    return downlines.filter((d) => keep.has(d.getUser().getUserStatus()));
  }, [downlines]);

  const subjects: IpSubject[] = React.useMemo(() => {
    const temp: IpSubject[] = [];

    // Users
    for (const d of activeDownlines) {
      const u = d.getUser();
      temp.push({
        id: u.getUserId(),
        name: `${u.getUserFirstname()} ${u.getUserLastname()}`,
        type: 'Agent',
        entity: u,
      });
    }

    // Staff
    for (const s of staffList) {
      temp.push({
        id: s.getStaffId(),
        name: `${s.getStaffFirstname()} ${s.getStaffLastname()}`,
        type: 'Staff',
        entity: s,
      });
    }

    temp.sort((a, b) => a.name.localeCompare(b.name));

    return temp;
  }, [activeDownlines, staffList]);

  /**
   * Fetch a page of agency downlines with their IP data.
   *
   * Supports initial load and incremental pagination, updating
   * the list, offset, and hasMore flags accordingly.
   *
   * @param startOffset - Zero-based offset for the page to load.
   * @param initial - Whether this is the initial load (replaces list instead of appending).
   */
  const fetchDownlines = React.useCallback(
    async (startOffset: number, initial = false): Promise<void> => {
      if (loadingMore || startOffset === lastFetchedOffsetRef.current) return;

      setLoadingMore(true);
      lastFetchedOffsetRef.current = startOffset;

      try {
        const opts: any = {
          include: ['ips'],
          limit: PAGE_SIZE,
          offset: startOffset,
          sortBy: 'userId',
        };
        const downlines = await agencyDownlineRepository.getAgencyLoasAndPrincipal(
          userId,
          agencyId,
          opts
        );

        const items: AgencyDownline[] = downlines.getItems();

        if (initial) {
          setDownlines(items);
        } else if (items.length) {
          setDownlines((prev) => prev.concat(items));
        }

        const total: number = downlines.getTotal();
        const next = startOffset + items.length;
        setOffset(next);
        setHasMore(next < total);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [agencyDownlineRepository, agencyId, userId, loadingMore]
  );

  /**
   * Load all staff for the agency with their IP data.
   *
   * Populates the local staff list used for IP management.
   */
  const fetchStaff = async (): Promise<void> => {
    const staff = await staffRepository.getAgencyStaff(userId, agencyId, {
      include: ['ips'],
    });

    const items: Staff[] = staff.getItems();
    setStaffList(items);
  };

  /**
   * Request the next page of downlines if available.
   *
   * Guards against duplicate loads by checking hasMore and loading state.
   */
  const onLoadMore = React.useCallback((): void => {
    if (!hasMore || loadingMore) return;
    void fetchDownlines(offset, false);
  }, [hasMore, loadingMore, offset, fetchDownlines]);

  /**
   * Set up an intersection observer on the sentinel element to drive
   * infinite scrolling. When the sentinel enters the viewport, the next
   * page is loaded and the sentinel is re-observed after the callback.
   */
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries, obs) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          obs.unobserve(entry.target);
          onLoadMore();
          queueMicrotask(() => el && obs.observe(el));
        }
      },
      { rootMargin: '800px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore]);

  /**
   * Reset pagination state and reload downlines and staff when the
   * agency or user context changes.
   */
  React.useEffect(() => {
    setDownlines([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    lastFetchedOffsetRef.current = -1;
    void fetchDownlines(0, true);
    void fetchStaff();
  }, [agencyId, userId]);

  /**
   * Extract a simple list of IP objects from a user entity.
   *
   * @param u - User entity exposing getIps().getItems().
   * @returns Array of `{ id, ip }` objects for the user's whitelisted IPs.
   */
  function readIpWhitelistsFromUser(u: any) {
    const items = u.getIps().getItems();

    return items.map((x: any) => ({
      id: x.getId(),
      ip: x.getIp(),
    }));
  }

  /**
   * Extract a simple list of IP objects from a staff entity.
   *
   * @param s - Staff entity exposing getIps().getItems().
   * @returns Array of `{ id, ip }` objects for the staff member's whitelisted IPs.
   */
  function readIpWhitelistsFromStaff(s: any) {
    const items = s.getIps().getItems();
    return items.map((x: any) => ({
      id: x.getId(),
      ip: x.getIp(),
    }));
  }

  /**
   * Read whitelisted IPs for a given subject, delegating to the
   * appropriate reader based on the subject type.
   *
   * @param subject - The subject (agent or staff) whose IPs should be read.
   * @returns Array of `{ id, ip }` objects for the subject's whitelisted IPs.
   */
  function readIpWhitelists(subject: IpSubject) {
    if (subject.type === 'Agent') {
      return readIpWhitelistsFromUser(subject.entity);
    }
    return readIpWhitelistsFromStaff(subject.entity);
  }

  return (
    <div className="col-12 px-2" id="ip-whitelist">
      <div className="card h-auto">
        <div className="card-header">User IP management</div>
        <div className="card-body">
          <>
            <div className="alert alert-info py-1" style={{ fontSize: '10pt' }}>
              <i className="far fa-info-circle mr-2"></i>Add allowed IP addresses for staff and LOA
              agents. They’ll only be able to sign in from the IPs listed. If no IPs are listed,
              access is allowed from any IP.{' '}
            </div>
            <div className="border rounded" style={{ maxHeight: 520, overflowY: 'auto' }}>
              {subjects.map((subject) => {
                const initialIps = readIpWhitelists(subject);

                return (
                  <UserIpWhitelistRow
                    key={`${subject.type}-${subject.id}`}
                    userAccessToken={userAccessToken}
                    subjectType={subject.type}
                    subjectId={subject.id}
                    subjectName={subject.name}
                    initialIps={initialIps}
                  />
                );
              })}

              {subjects.length === 0 && !loading && (
                <div className="p-3 text-muted">No agents or staff found.</div>
              )}

              {loadingMore && (
                <div className="p-2 text-center">
                  <i className="fal fa-spinner fa-spin" /> Loading…
                </div>
              )}

              {!loadingMore && hasMore && (
                <div className="p-2 text-center">
                  <button className="btn btn-sm btn-outline-primary" onClick={onLoadMore}>
                    Load more
                  </button>
                </div>
              )}

              <div ref={sentinelRef} />
            </div>
          </>
        </div>
      </div>
    </div>
  );
};
